import { Injectable, signal } from '@angular/core';

/** Preferred recording MIME type — MP4/AAC for universal playback. */
const PREFERRED_MIME = 'audio/mp4';
/** Fallback MIME type — WebM/Opus (Firefox, older Chrome). */
const FALLBACK_MIME = 'audio/webm;codecs=opus';

/**
 * Service that ensures all audio is stored as MP4/AAC for universal playback.
 *
 * - Detects the best recording format for the current browser
 * - Lazy-loads ffmpeg.wasm only when transcoding is needed (~8.5MB gzipped)
 * - Transcodes WebM → MP4/AAC before upload
 */
@Injectable({ providedIn: 'root' })
export class AudioTranscodeService {
  private ffmpeg: import('@ffmpeg/ffmpeg').FFmpeg | null = null;
  private loadPromise: Promise<void> | null = null;
  private transcodeQueue: Promise<Blob> = Promise.resolve(new Blob());

  /** True while transcoding is in progress */
  readonly transcoding = signal(false);

  /**
   * Returns the best MIME type for recording on this browser.
   * Prefers audio/mp4 (no transcoding needed). Falls back to audio/webm.
   */
  getRecorderMimeType(): string {
    if (typeof MediaRecorder !== 'undefined') {
      if (MediaRecorder.isTypeSupported(PREFERRED_MIME)) return PREFERRED_MIME;
      if (MediaRecorder.isTypeSupported(FALLBACK_MIME)) return FALLBACK_MIME;
      if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
    }
    return '';
  }

  /** Whether the given blob needs transcoding to MP4 before upload. */
  needsTranscode(blob: Blob): boolean {
    return !blob.type.includes('mp4') && !blob.type.includes('mpeg');
  }

  /**
   * Ensures the blob is in MP4/AAC format.
   * If already MP4, returns as-is. Otherwise transcodes via ffmpeg.wasm.
   */
  async ensureMp4(blob: Blob): Promise<Blob> {
    if (!this.needsTranscode(blob)) return blob;

    // Serialize transcodes to avoid ffmpeg virtual filesystem conflicts
    const result = this.transcodeQueue.then(
      () => this.doTranscode(blob),
      () => this.doTranscode(blob)
    );
    this.transcodeQueue = result;
    return result;
  }

  private async doTranscode(blob: Blob): Promise<Blob> {
    this.transcoding.set(true);
    try {
      await this.loadFFmpeg();
      return await this.transcode(blob);
    } finally {
      this.transcoding.set(false);
    }
  }

  /** Lazy-load ffmpeg.wasm single-thread core from CDN. */
  private async loadFFmpeg(): Promise<void> {
    if (this.ffmpeg?.loaded) return;
    if (this.loadPromise) {
      await this.loadPromise;
      return;
    }

    this.loadPromise = (async () => {
      try {
        const { FFmpeg } = await import('@ffmpeg/ffmpeg');
        const { toBlobURL } = await import('@ffmpeg/util');

        this.ffmpeg = new FFmpeg();

        const baseURL = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd';
        await this.ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
      } catch (err) {
        this.loadPromise = null;
        this.ffmpeg = null;
        throw err;
      }
    })();

    await this.loadPromise;
  }

  /** Transcode a non-MP4 audio blob to MP4/AAC. */
  private async transcode(blob: Blob): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg not loaded');

    const { fetchFile } = await import('@ffmpeg/util');

    const inputName = blob.type.includes('webm') ? 'input.webm' : 'input.ogg';
    await this.ffmpeg.writeFile(inputName, await fetchFile(blob));

    await this.ffmpeg.exec([
      '-i', inputName,
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', 'faststart',
      'output.mp4',
    ]);

    const data = await this.ffmpeg.readFile('output.mp4');
    const bytes = data instanceof Uint8Array ? data.slice() : new TextEncoder().encode(data as string);
    const mp4Blob = new Blob([bytes], { type: 'audio/mp4' });

    await this.ffmpeg.deleteFile(inputName);
    await this.ffmpeg.deleteFile('output.mp4');

    return mp4Blob;
  }
}
